import Link from "next/link";
import BusinessAvatarSurface from "@/components/business/BusinessAvatarSurface";
import {
  getBusinessAvatarImage,
  resolveBusinessCoverUrl,
} from "@/lib/businessImages";
import { getBusinessPublicUrl } from "@/lib/ids/publicRefs";
import styles from "./HomepageEditorial.module.css";

export type EditorialBusiness = {
  id?: string | null;
  public_id?: string | null;
  business_name?: string | null;
  business_type?: string | null;
  business_type_name?: string | null;
  category?: string | null;
  city?: string | null;
  state?: string | null;
  profile_photo_url?: string | null;
  avatar_url?: string | null;
  logo_url?: string | null;
  cover_photo_url?: string | null;
  avatar_media_asset_id?: string | null;
  business_avatar_media_asset?: Record<string, unknown> | null;
  business_cover_media_asset?: Record<string, unknown> | null;
  media_assets?: Array<Record<string, unknown>> | null;
  distanceMiles?: number | null;
};

export function getEditorialBusinessAvatar(business: EditorialBusiness) {
  const avatar = getBusinessAvatarImage(business);
  if (avatar.kind === "image") return avatar;

  // The shared avatar helper already filters placeholder and private-looking
  // candidates. A public storefront cover is the compact-card fallback before
  // the existing initials treatment.
  const coverSrc = resolveBusinessCoverUrl(business);
  return coverSrc ? { kind: "image" as const, src: coverSrc } : avatar;
}

function getBusinessMeta(business: EditorialBusiness) {
  const type = String(
    business?.business_type_name ||
      business?.business_type ||
      business?.category ||
      ""
  ).trim();
  const city = String(business?.city || "").trim();
  const distance =
    typeof business?.distanceMiles === "number" &&
    Number.isFinite(business.distanceMiles)
      ? `${business.distanceMiles.toFixed(1)} mi`
      : "";

  return [type, city, distance].filter(Boolean).slice(0, 2).join(" · ");
}

export default function EditorialBusinessCard({
  business,
}: {
  business: EditorialBusiness;
}) {
  const businessName =
    String(business?.business_name || "").trim() || "Local business";
  const meta = getBusinessMeta(business) || "Independent local business";
  const avatar = getEditorialBusinessAvatar(business);

  return (
    <Link
      href={getBusinessPublicUrl(business)}
      prefetch={false}
      className={styles.businessCard}
      aria-label={`${businessName}, ${meta}`}
    >
      <span className={styles.businessImage}>
        <BusinessAvatarSurface
          business={business}
          avatar={avatar}
          alt=""
          sizes="72px"
          compact
          variant="avatar"
        />
      </span>
      <span className={styles.businessCopy}>
        <span className={styles.businessName}>{businessName}</span>
        <span className={styles.businessMeta}>{meta}</span>
      </span>
      <span className={styles.businessArrow} aria-hidden="true">
        →
      </span>
    </Link>
  );
}
